<template>
  <section class="pp-featured-section">
    <div class="container">

      <!-- SECTION HEADER -->
      <div class="pp-featured-header">
        <div class="pp-featured-title">
          <span class="pp-section-eyebrow">
            SÉLECTION PRODUPNEU
          </span>

          <h2>
            Nos pneus <span>en vedette</span>
          </h2>

          <p>
            Découvrez notre sélection de pneus et trouvez
            le modèle adapté à votre véhicule.
          </p>
        </div>

        <router-link to="/products" class="pp-view-products">
          Voir tous les pneus
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M4 12h16M13 5l7 7-7 7" />
          </svg>
        </router-link>
      </div>

      <!-- PRODUCTS GRID -->
      <div class="pp-featured-grid">

        <article v-for="(product, index) in featuredProducts" :key="product.id" class="pp-featured-card">

          <!-- PRODUCT IMAGE AREA -->
          <div class="pp-card-visual">

            <!-- BADGE -->
            <span class="pp-card-badge" :class="{ 'badge-dark': index % 2 === 1 }">
              {{ badges[index] }}
            </span>

            <div class="pp-image-holder">
              <img v-if="!failedImages[product.id] && product.image" :src="product.image"
                :alt="product.brand + ' ' + product.name" loading="lazy" @error="imageError(product.id)" />

              <div v-else class="pp-image-fallback">
                <svg viewBox="0 0 100 120" fill="none">
                  <rect x="18" y="4" width="64" height="112" rx="24" stroke="currentColor" stroke-width="5" />
                  <path d="M20 22 40 12 60 26 80 16
                       M20 46 40 36 60 50 80 40
                       M20 70 40 60 60 74 80 64
                       M20 94 40 84 60 98 80 88" stroke="currentColor" stroke-width="4" />
                </svg>
                <small>Photo à ajouter</small>
              </div>
            </div>

            <!-- EU LABEL -->
            <div class="pp-eu-mini">
              <div class="pp-eu-heading">
                <span class="pp-eu-flag">EU</span>
                Étiquette
              </div>

              <div class="pp-eu-row">
                <div class="pp-energy-bars">
                  <span v-for="i in 5" :key="i"></span>
                </div>
                <span class="pp-eu-letter" :class="gradeClass(product.eu.fuel)">
                  {{ product.eu.fuel || "—" }}
                </span>
              </div>

              <div class="pp-eu-row">
                <div class="pp-energy-bars">
                  <span v-for="i in 5" :key="i"></span>
                </div>
                <span class="pp-eu-letter" :class="gradeClass(product.eu.wet)">
                  {{ product.eu.wet || "—" }}
                </span>
              </div>

              <div class="pp-eu-noise">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M3 9h4l5-4v14l-5-4H3z" />
                  <path d="M16 9a4 4 0 0 1 0 6" />
                  <path d="M19 6a8 8 0 0 1 0 12" />
                </svg>
                <strong>
                  {{ product.eu.noise != null
                    ? product.eu.noise + " dB"
                    : "—" }}
                </strong>
              </div>
            </div>
          </div>

          <!-- PRODUCT DETAILS -->
          <div class="pp-card-content">

            <div class="pp-product-brand">
              {{ product.brand }}
            </div>

            <h3>{{ product.name }}</h3>

            <div class="pp-product-size">
              {{ product.size }}
              {{ product.load_index }}{{ product.speed_index }}
            </div>

            <div class="pp-product-features">
              <div>
                <span class="pp-feature-icon" :class="{ winter: product.season === 'winter' }">
                  <svg v-if="product.season === 'summer'" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none">
                    <path d="M12 2v20M4 6l16 12M4 18 20 6" />
                    <path d="m9 5 3 3 3-3M9 19l3-3 3 3" />
                  </svg>
                </span>

                <span>{{ seasonLabel(product.season) }}</span>
              </div>

              <div>
                <span class="pp-feature-icon vehicle">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M5 15 3 13l2-7h14l2 7-2 2" />
                    <path d="M3 13h18v5H3z" />
                    <circle cx="7" cy="18" r="1" />
                    <circle cx="17" cy="18" r="1" />
                  </svg>
                </span>
                <span>Voiture particulière</span>
              </div>
            </div>

            <!-- DEMO INDICATOR, NOT FAKE REVIEWS -->
            <div class="pp-product-highlight">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="m12 2 2.9 6.1 6.7.9-4.8 4.6 1.2 6.7L12 17.1l-6 3.2 1.2-6.7L2.4 9l6.7-.9z" />
              </svg>
              Sélection ProduPneu
            </div>

            <!-- PRICE -->
            <div class="pp-price-line">
              <div class="pp-price">
                <strong>{{ formatPrice(product.price) }}</strong>
                <small>Prix unitaire TTC · Démo</small>
              </div>

              <span class="pp-stock" :class="{ unavailable: product.stock <= 0 }">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" />
                  <path v-if="product.stock > 0" d="m7 12 3 3 7-7" />
                  <path v-else d="M8 8l8 8M16 8l-8 8" />
                </svg>
                {{ product.stock > 0 ? "En stock" : "Indisponible" }}
              </span>
            </div>

            <!-- CART -->
            <button type="button" class="pp-add-cart" :disabled="product.stock <= 0" @click="addToCart(product)">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M3 4h2l2.5 12h11L21 8H6" />
                <circle cx="10" cy="20" r="1" />
                <circle cx="18" cy="20" r="1" />
              </svg>

              {{ addedProduct === product.id
                ? "Ajouté au panier ✓"
                : "Ajouter au panier" }}
            </button>

          </div>
        </article>

      </div>

      <!-- BOTTOM MOBILE CTA -->
      <div class="pp-mobile-view-all">
        <router-link to="/products">
          Découvrir tous nos pneus →
        </router-link>
      </div>

    </div>
  </section>
</template>

<script>
import products from "../../data/products";

export default {
  name: "FeaturedProducts",

  data() {
    return {
      badges: [
        "À découvrir",
        "Notre sélection",
        "Pneu été",
        "4 saisons",
      ],

      selectedIds: [1, 2, 4, 5],

      failedImages: {},
      addedProduct: null,
      resetTimer: null,
    };
  },

  computed: {
    featuredProducts() {
      return this.selectedIds
        .map((id) => products.find((p) => p.id === id))
        .filter(Boolean);
    },
  },

  beforeDestroy() {
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
    }
  },

  methods: {
    imageError(id) {
      this.$set(this.failedImages, id, true);
    },

    gradeClass(grade) {
      const g = String(grade || "").toUpperCase();

      return ["A", "B", "C", "D", "E"].includes(g)
        ? "grade-" + g.toLowerCase()
        : "grade-unknown";
    },

    seasonLabel(season) {
      return {
        summer: "Pneu été",
        winter: "Pneu hiver",
        "all-season": "Pneu 4 saisons",
      }[season] || "Pneu";
    },

    formatPrice(price) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(price);
    },


    async addToCart(product) {
      if (!product) return;

      if (Number(product.stock) === 0) {
        this.$store.dispatch("toast/show", {
          type: "warning",
          title: "Produit indisponible",
          message: "Ce produit est actuellement en rupture de stock.",
          duration: 3000,
        });
        return;
      }

      try {
        await this.$store.dispatch("cart/addItem", product);

        // Keep existing button animation
        this.addedProduct = product.id;

        if (this.resetTimer) {
          clearTimeout(this.resetTimer);
        }

        this.resetTimer = setTimeout(() => {
          this.addedProduct = null;
          this.resetTimer = null;
        }, 1800);

        // Global success notification
        this.$store.dispatch("toast/show", {
          type: "success",
          title: "Produit ajouté au panier !",
          message: `${product.brand} ${product.name} a bien été ajouté à votre panier.`,
          duration: 3000,
        });

      } catch (error) {
        console.error("Erreur panier :", error);

        this.$store.dispatch("toast/show", {
          type: "error",
          title: "Erreur",
          message: "Impossible d'ajouter ce produit au panier.",
          duration: 4000,
        });
      }
    },

  },
};
</script>

<style scoped>
.pp-featured-section {
  padding: 78px 0 85px;
  background: #fff;
  font-family: "Poppins", sans-serif;
}

.pp-featured-section *,
.pp-featured-section *::before,
.pp-featured-section *::after {
  box-sizing: border-box;
}

/* HEADER */

.pp-featured-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 25px;
  margin-bottom: 32px;
}

.pp-section-eyebrow {
  display: block;
  color: #ff7900;
  font-weight: 800;
  font-size: 10px;
  letter-spacing: 2px;
  margin-bottom: 12px;
}

.pp-featured-title h2 {
  font-size: clamp(27px, 2.7vw, 37px);
  font-weight: 900;
  color: #181d22;
  line-height: 1.25;
  letter-spacing: -1px;
  margin: 0 0 10px;
}

.pp-featured-title h2 span {
  color: #ff7900;
}

.pp-featured-title p {
  font-size: 13px;
  color: #7e858c;
  line-height: 1.7;
  margin: 0;
}

.pp-view-products {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #ff7900;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  text-decoration: none !important;
  padding-bottom: 5px;
}

.pp-view-products:hover {
  color: #e86d00;
}

.pp-view-products svg {
  width: 18px;
  height: 18px;
  stroke: currentColor;
  stroke-width: 2;
}

/* GRID */

.pp-featured-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

/* CARD */

.pp-featured-card {
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e9ebef;
  border-radius: 15px;
  box-shadow: 0 3px 12px rgba(16, 24, 32, .035);
  overflow: hidden;
  transition:
    transform .3s ease,
    box-shadow .3s ease,
    border-color .3s ease;
}

.pp-featured-card:hover {
  transform: translateY(-6px);
  border-color: #ffd5b7;
  box-shadow: 0 17px 38px rgba(20, 27, 35, .105);
}

/* IMAGE */

.pp-card-visual {
  position: relative;
  height: 205px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 30px 92px 0 10px;
  background:
    radial-gradient(circle at 48% 82%, #f2f4f6, transparent 75%),
    #fff;
}

.pp-card-badge {
  position: absolute;
  top: 15px;
  left: 14px;
  padding: 7px 13px;
  border-radius: 50px;
  color: #fff;
  background: #ff7900;
  font-size: 10px;
  font-weight: 800;
  white-space: nowrap;
  z-index: 2;
}

.pp-card-badge.badge-dark {
  background: #171c22;
}

.pp-image-holder {
  height: 168px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.pp-image-holder img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform .4s ease;
}

.pp-featured-card:hover .pp-image-holder img {
  transform: scale(1.06);
}

.pp-image-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  color: #b3b7bb;
}

.pp-image-fallback svg {
  width: 90px;
  height: 120px;
}

.pp-image-fallback small {
  font-size: 9px;
}

/* EU MINI LABEL */

.pp-eu-mini {
  position: absolute;
  top: 51px;
  right: 12px;
  width: 70px;
  padding: 7px 6px;
  border: 1px solid #e6e9ed;
  border-radius: 7px;
  background: #fff;
  box-shadow: 0 3px 11px rgba(0, 0, 0, .035);
}

.pp-eu-heading {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 7px;
  color: #68717a;
  margin-bottom: 6px;
}

.pp-eu-flag {
  color: #fff;
  background: #234a9b;
  font-size: 7px;
  font-weight: 800;
  padding: 2px;
  border-radius: 2px;
}

.pp-eu-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3px;
  margin-bottom: 5px;
}

.pp-energy-bars {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 34px;
}

.pp-energy-bars span {
  height: 4px;
  border-radius: 1px;
}

.pp-energy-bars span:nth-child(1) {
  width: 17px;
  background: #168c43;
}

.pp-energy-bars span:nth-child(2) {
  width: 21px;
  background: #82be27;
}

.pp-energy-bars span:nth-child(3) {
  width: 25px;
  background: #e1d42d;
}

.pp-energy-bars span:nth-child(4) {
  width: 29px;
  background: #f2a01d;
}

.pp-energy-bars span:nth-child(5) {
  width: 33px;
  background: #e7472b;
}

.pp-eu-letter {
  min-width: 21px;
  text-align: center;
  border-radius: 3px;
  font-weight: 900;
  color: #fff;
  background: #191f25;
  font-size: 11px;
  padding: 3px 2px;
}

.pp-eu-letter.grade-unknown {
  background: #9ca3af;
}

.pp-eu-noise {
  border-top: 1px solid #e9edf0;
  padding-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #2455a4;
}

.pp-eu-noise svg {
  width: 15px;
  height: 15px;
  stroke: currentColor;
  stroke-width: 2;
}

.pp-eu-noise strong {
  font-size: 9px;
  white-space: nowrap;
}

/* PRODUCT CONTENT */

.pp-card-content {
  padding: 10px 17px 17px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.pp-product-brand {
  font-size: 12px;
  font-weight: 800;
  color: #343b42;
  margin-bottom: 3px;
}

.pp-card-content h3 {
  font-size: 15px;
  font-weight: 850;
  line-height: 1.4;
  min-height: 42px;
  margin: 0 0 10px;
  color: #161c21;
}

.pp-product-size {
  font-size: 12px;
  font-weight: 600;
  color: #555e66;
  margin-bottom: 12px;
}

.pp-product-features {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pp-product-features>div {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #69727a;
}

.pp-feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: #ff9700;
}

.pp-feature-icon.winter {
  color: #2877c7;
}

.pp-feature-icon.vehicle {
  color: #687581;
}

.pp-feature-icon svg {
  width: 18px;
  height: 18px;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* HIGHLIGHT */

.pp-product-highlight {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #e98900;
  margin-top: auto;
  padding-top: 17px;
  padding-bottom: 8px;
  font-size: 11px;
  font-weight: 750;
}

.pp-product-highlight svg {
  width: 17px;
  height: 17px;
  stroke: currentColor;
  stroke-width: 1.7;
}

/* PRICE */

.pp-price-line {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 12px;
}

.pp-price {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.pp-price strong {
  font-size: 25px;
  font-weight: 900;
  line-height: 1.1;
  color: #171c21;
  letter-spacing: -.6px;
}

.pp-price small {
  color: #999fa5;
  font-size: 9px;
}

.pp-stock {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #168248;
  font-size: 10px;
  font-weight: 800;
  white-space: nowrap;
  padding-bottom: 5px;
}

.pp-stock.unavailable {
  color: #ad4848;
}

.pp-stock svg {
  width: 15px;
  height: 15px;
  stroke: currentColor;
  stroke-width: 2;
}

/* BUTTON */

.pp-add-cart {
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: 8px;
  background: #ff7900;
  color: #fff;
  font-family: inherit;
  font-size: 12px;
  font-weight: 850;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  transition: background .2s, transform .2s;
}

.pp-add-cart:hover:not(:disabled) {
  background: #e86e00;
  transform: translateY(-2px);
}

.pp-add-cart:disabled {
  background: #d5d9de;
  cursor: not-allowed;
}

.pp-add-cart svg {
  width: 19px;
  height: 19px;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.pp-mobile-view-all {
  display: none;
}

/* RESPONSIVE */

@media (max-width: 1199px) {
  .pp-featured-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pp-card-visual {
    height: 240px;
  }

  .pp-image-holder {
    height: 200px;
  }
}

@media (max-width: 767px) {
  .pp-featured-section {
    padding: 55px 0 60px;
  }

  .pp-featured-header {
    align-items: flex-start;
    margin-bottom: 25px;
  }

  .pp-featured-title h2 {
    font-size: 28px;
  }

  .pp-view-products {
    display: none;
  }

  .pp-featured-grid {
    gap: 12px;
  }

  .pp-mobile-view-all {
    display: block;
    text-align: center;
    margin-top: 25px;
  }

  .pp-mobile-view-all a {
    display: inline-block;
    padding: 13px 23px;
    border: 1px solid #ff7900;
    border-radius: 8px;
    color: #ff7900;
    font-size: 12px;
    font-weight: 800;
  }
}

@media (max-width: 575px) {
  .pp-featured-grid {
    grid-template-columns: 1fr;
  }

  .pp-card-visual {
    height: 240px;
  }

  .pp-image-holder {
    height: 195px;
  }

  .pp-card-content {
    padding: 14px 19px 19px;
  }
}

@media (prefers-reduced-motion: reduce) {

  .pp-featured-card,
  .pp-image-holder img,
  .pp-add-cart {
    transition: none;
  }
}
</style>
